/**
 * LiveKitLinkGrid — BROADCASTER-side view for linked (multi-broadcaster)
 * sessions on LiveKit. Replaces the Agora mesh grid: publishes this
 * broadcaster's camera+mic into the shared linked room and renders every
 * broadcaster (self mirrored + remotes) with the same no-zoom aspect logic
 * as the viewer grid.
 *
 *   <LiveKitLinkGrid
 *     token={token} serverUrl={LIVEKIT_URL} roomName={`linked_${sessionId}`}
 *     selfInfo={{ user_id, name, picture }} partners={[{id,name,picture}]}
 *     micMuted={isMicMuted} camOff={isCamOff}
 *     onRoomReady={(room)=>{ linkRoomRef.current = room; }}
 *     onError={(e)=>...} onPartnerCount={(n)=>...}
 *   />
 *
 * Controls: parent flips micMuted / camOff props (wired to the existing
 * Mute / camera buttons) — the grid applies them to the local participant.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Room, RoomEvent, Track, VideoPresets, createLocalVideoTrack, createLocalAudioTrack } from 'livekit-client';

function Tile({ track, label, picture, mirrored, muted, forceCover = false }) {
  const videoRef = useRef(null);
  const bgRef = useRef(null);
  const [fit, setFit] = useState('cover');

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !track) return;
    track.attach(el);
    el.play?.().catch(() => {});
    if (bgRef.current) track.attach(bgRef.current);
    const decideFit = () => {
      if (forceCover) { setFit('cover'); return; }
      const vw = el.videoWidth, vh = el.videoHeight;
      const r = el.parentElement?.getBoundingClientRect();
      if (!vw || !vh || !r?.width || !r?.height) return;
      setFit((vh > vw) === (r.height > r.width) ? 'cover' : 'contain');
    };
    el.addEventListener('loadedmetadata', decideFit);
    el.addEventListener('resize', decideFit);
    decideFit();
    return () => {
      el.removeEventListener('loadedmetadata', decideFit);
      el.removeEventListener('resize', decideFit);
      try { track.detach(el); } catch {}
      if (bgRef.current) { try { track.detach(bgRef.current); } catch {} }
    };
  }, [track, forceCover]);

  const flip = mirrored ? ' scale-x-[-1]' : '';
  return (
    <div className="relative w-full h-full overflow-hidden bg-black">
      {fit === 'contain' && (
        <video ref={bgRef} autoPlay playsInline muted
          className={`absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50${flip}`} />
      )}
      <video ref={videoRef} autoPlay playsInline muted={muted}
        className={`absolute inset-0 w-full h-full${flip}`} style={{ objectFit: fit }} />
      <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 bg-black/55 rounded-full pl-1 pr-2.5 py-0.5 backdrop-blur-sm">
        {picture ? (
          <img src={picture} alt="" className="w-5 h-5 rounded-full object-cover" />
        ) : (
          <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center text-[10px] text-white font-bold">
            {(label || '?')[0]}
          </div>
        )}
        <span className="text-white text-[11px] font-semibold truncate max-w-[90px]">{label}</span>
      </div>
    </div>
  );
}

// Round 63: every face fully visible, up to SIXTEEN broadcasters (a Party).
export function partyGridClass(total) {
  if (total <= 4) return 'grid-cols-2 grid-rows-2';
  if (total <= 6) return 'grid-cols-3 grid-rows-2';
  if (total <= 9) return 'grid-cols-3 grid-rows-3';
  if (total <= 12) return 'grid-cols-4 grid-rows-3';
  return 'grid-cols-4 grid-rows-4';
}

export default function LiveKitLinkGrid({
  token, serverUrl, roomName, selfInfo, partners = [],
  micMuted = false, camOff = false,
  layout = 'auto', // 'auto' | 'strip' | 'sides' — strip = single horizontal row of N equal cols; sides = board-game desktop layout (2 broadcasters pinned to L/R edges with a transparent middle for the board)
  onRoomReady, onError, onPartnerCount, onDisconnected, onParticipantLost,
}) {
  const roomRef = useRef(null);
  const micMutedRef = useRef(micMuted);
  useEffect(() => { micMutedRef.current = micMuted; }, [micMuted]);
  const [localTrack, setLocalTrack] = useState(null);
  const [remotes, setRemotes] = useState([]); // [{identity, track}]
  const [connected, setConnected] = useState(false);

  const nameFor = useCallback((identity) => {
    const p = (partners || []).find((x) => x.id === identity);
    return p?.name || 'Guest';
  }, [partners]);
  const picFor = useCallback(
    (identity) => (partners || []).find((x) => x.id === identity)?.picture,
    [partners]
  );

  useEffect(() => {
    if (!token || !serverUrl) return;
    let cancelled = false;

    // Permanent lifecycle beacons — the "grid never mounted" class of
    // regression must never be invisible again. If you see a MOUNTED
    // without a matching UNMOUNTED per session, the mesh is healthy.
    console.log('[LinkGrid] MOUNTED room=', roomName, 'serverUrl=', serverUrl);

    const room = new Room({
      adaptiveStream: false,
      dynacast: true,
      publishDefaults: {
        videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 },
        simulcast: true,
      },
    });
    roomRef.current = room;

    const rebuild = () => {
      if (cancelled) return;
      const next = [];
      room.remoteParticipants.forEach((p) => {
        let video = null;
        p.videoTrackPublications.forEach((pub) => { if (pub.track) video = pub.track; });
        p.audioTrackPublications.forEach((pub) => { if (pub.track) { try { pub.track.attach(); } catch {} } });
        if (video) next.push({ identity: p.identity, track: video });
      });
      next.sort((a, b) => nameFor(a.identity).localeCompare(nameFor(b.identity)));
      console.log('[LinkGrid] tiles', next.map((r) => r.identity));
      setRemotes(next);
      if (onPartnerCount) onPartnerCount(next.length);
    };

    room
      .on(RoomEvent.TrackSubscribed, rebuild)
      .on(RoomEvent.TrackUnsubscribed, rebuild)
      .on(RoomEvent.ParticipantConnected, rebuild)
      .on(RoomEvent.ParticipantDisconnected, (p) => {
        rebuild();
        // Surface TRUE departures to the parent (drop detector) — fires
        // only when a participant's connection actually leaves the room.
        try { onParticipantLost && onParticipantLost(p?.identity); } catch { /* noop */ }
      })
      .on(RoomEvent.Reconnecting, () => console.log('[LinkGrid] reconnecting…'))
      .on(RoomEvent.Reconnected, () => { console.log('[LinkGrid] reconnected'); rebuild(); })
      .on(RoomEvent.Disconnected, (reason) => {
        console.warn('[LinkGrid] DISCONNECTED reason=', reason);
        if (!cancelled) { setConnected(false); onDisconnected && onDisconnected(reason); }
      });

    (async () => {
      try {
        await room.connect(serverUrl, token);
        if (cancelled) return;
        // 720p capture: high enough for crisp tiles, kind to upload bandwidth
        // when 2–4 broadcasters publish at once.
        const cam = await createLocalVideoTrack({
          facingMode: 'user',
          resolution: VideoPresets.h720.resolution,
        });
        const mic = await createLocalAudioTrack({ echoCancellation: true, noiseSuppression: true });
        if (cancelled) { cam.stop(); mic.stop(); return; }
        await room.localParticipant.publishTrack(cam, { source: Track.Source.Camera });
        await room.localParticipant.publishTrack(mic, { source: Track.Source.Microphone });
        if (micMutedRef.current) { await room.localParticipant.setMicrophoneEnabled(false); }
        setLocalTrack(cam);
        setConnected(true);
        rebuild();
        if (onRoomReady) onRoomReady(room);
      } catch (e) {
        if (!cancelled && onError) onError(e);
      }
    })();

    return () => {
      cancelled = true;
      console.log('[LinkGrid] UNMOUNTED room=', roomName);
      try { room.disconnect(); } catch {}
      roomRef.current = null;
    };
  }, [token, serverUrl, roomName]);

  // Parent-driven mic/cam toggles (wired to the existing control buttons).
  useEffect(() => {
    const room = roomRef.current;
    if (!room || !connected) return;
    room.localParticipant.setMicrophoneEnabled(!micMuted).catch(() => {});
  }, [micMuted, connected]);
  useEffect(() => {
    const room = roomRef.current;
    if (!room || !connected) return;
    room.localParticipant.setCameraEnabled(!camOff).catch(() => {});
  }, [camOff, connected]);

  const total = (localTrack ? 1 : 0) + remotes.length;
  // Layout rules:
  //   layout="strip" — ALL tiles in one horizontal row of N equal columns
  //     (used by the in-game linked layout so the board can sit below).
  //   layout="sides" — desktop board-games with exactly 2 broadcasters:
  //     tiles pinned to the LEFT and RIGHT edges, transparent middle for
  //     the PARENT-rendered board. Falls back to 'strip' with 3+ or when
  //     the local track hasn't published yet.
  //   layout="auto"  — 1: full; 2: side-by-side (never stacked); 3: 2×2
  //     with the 3rd tile spanning both columns of row 2; 4: 2×2 quadrants.
  const effectiveLayout =
    layout === 'sides' && !(localTrack && remotes.length === 1) ? 'strip' : layout;

  // Every multi-tile layout FILLS its cell (Aug 18, 2026 fix): before
  // forceCover a landscape webcam inside a portrait half-cell letterboxed
  // into a tiny box while the phone's portrait camera filled its cell.
  const tileForceCover = total >= 2;

  let gridClass;
  let extraChildClass = '';
  if (effectiveLayout === 'strip') {
    // Round 63: a Party puts up to 16 faces in the strip — two short rows past 8
    const cols = Math.max(1, Math.min(8, total <= 8 ? total : Math.ceil(total / 2)));
    const colsMap = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6', 7: 'grid-cols-7', 8: 'grid-cols-8' };
    gridClass = `${colsMap[cols]} ${total > 8 ? 'grid-rows-2' : 'grid-rows-1'}`;
  } else if (total <= 1) {
    gridClass = 'grid-cols-1 grid-rows-1';
  } else if (total === 2) {
    gridClass = 'grid-cols-2 grid-rows-1';
  } else if (total === 3) {
    // 3-way (Feb 2026): SELF takes the full left half (first child = the
    // local tile), the two partners stack on the right — no more one tile
    // stretched across the whole bottom row.
    gridClass = 'grid-cols-2 grid-rows-2';
    extraChildClass = '[&>*:nth-child(1)]:row-span-2';
  } else {
    gridClass = partyGridClass(total);   // Round 63: 4 → 2×2 … 16 → 4×4
  }

  return (
    <div className="w-full h-full bg-black" data-testid="livekit-link-grid">
      {!connected ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Joining the link…</p>
          </div>
        </div>
      ) : effectiveLayout === 'sides' ? (
        <div className="w-full h-full flex items-stretch justify-between gap-2 pointer-events-none"
             data-testid="livekit-link-sides">
          <div className="w-[28%] h-full pointer-events-auto overflow-hidden rounded-r-lg">
            <Tile track={localTrack} label={`${selfInfo?.name || 'You'} (You)`}
                  picture={selfInfo?.picture} mirrored muted forceCover />
          </div>
          <div className="w-[28%] h-full pointer-events-auto overflow-hidden rounded-l-lg">
            <Tile track={remotes[0].track} label={nameFor(remotes[0].identity)}
                  picture={picFor(remotes[0].identity)} muted={false} forceCover />
          </div>
        </div>
      ) : (
        <div className={`grid gap-0.5 w-full h-full ${gridClass} ${extraChildClass}`}>
          {localTrack && (
            <Tile
              track={localTrack}
              label={`${selfInfo?.name || 'You'} (You)`}
              picture={selfInfo?.picture}
              mirrored
              muted
              forceCover={tileForceCover}
            />
          )}
          {remotes.map((r) => (
            <Tile key={r.identity} track={r.track} label={nameFor(r.identity)} picture={picFor(r.identity)} muted={false} forceCover={tileForceCover} />
          ))}
        </div>
      )}
    </div>
  );
}
